<template>
    <div class="csv-import" :style="{ '--import-accent': primaryColor }">
        <!-- ============ RESULT OF ONE IMPORT ============ -->
        <template v-if="current">
            <div class="import-head">
                <v-icon size="20" class="icon-themed">fa-regular fa-file-csv</v-icon>
                <div class="import-head__text">
                    <span class="import-head__name">{{ current.fileName || $t('csvImport_importNumber', { id: current.importId }) }}</span>
                    <span class="import-head__meta">{{ importMeta(current) }}</span>
                </div>
                <span class="pill" :class="importPillClass(current.status)">
                    <v-icon v-if="isActive(current.status)" size="10" class="pill__spin">fa-regular fa-spinner-third</v-icon>
                    {{ $t('csvImport_status_' + current.status) }}
                </span>
            </div>

            <div v-if="current.status === 'COMPLETED' && current.enrolled > 0" class="notice notice--success" role="status">
                <v-icon size="18" class="notice__icon">fa-regular fa-circle-check</v-icon>
                <span>{{ $t('csvImport_completedSummary', { count: current.enrolled }) }}</span>
            </div>
            <div v-else-if="current.status === 'FAILED'" class="notice notice--error" role="alert">
                <v-icon size="18" class="notice__icon">fa-regular fa-circle-exclamation</v-icon>
                <span class="notice__body">
                    <strong>{{ $t('csvImport_stoppedTitle') }}</strong>
                    <span v-if="current.errorMessage">{{ current.errorMessage }}</span>
                    <span>{{ $t('csvImport_stoppedDesc') }}</span>
                </span>
            </div>

            <!-- Progress: registered + failed over the total; the grey rest is still in progress -->
            <div class="progress">
                <div class="progress__labels">
                    <span>{{ $t('csvImport_processed', { done: processedCount, total: current.totalRows || 0 }) }}</span>
                    <strong>{{ progressPercent }}%</strong>
                </div>
                <div class="progress__track" role="progressbar" :aria-valuenow="progressPercent" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress__seg progress__seg--ok" :style="{ width: segmentWidth(current.enrolled) }"></div>
                    <div class="progress__seg progress__seg--failed" :style="{ width: segmentWidth(current.failed) }"></div>
                </div>
            </div>

            <div class="counters">
                <div class="counter">
                    <span class="counter__label"><v-icon size="14" class="counter__icon counter__icon--ok">fa-regular fa-circle-check</v-icon>{{ $t('csvImport_registered') }}</span>
                    <span class="counter__value">{{ current.enrolled || 0 }}</span>
                </div>
                <div class="counter" :class="{ 'counter--muted': !current.pending }">
                    <span class="counter__label"><v-icon size="14" class="counter__icon counter__icon--pending">fa-regular fa-clock</v-icon>{{ $t('csvImport_inProgress') }}</span>
                    <span class="counter__value">{{ current.pending || 0 }}</span>
                </div>
                <div class="counter" :class="{ 'counter--alert': !isActive(current.status) && current.failed > 0 }">
                    <span class="counter__label"><v-icon size="14" class="counter__icon counter__icon--failed">fa-regular fa-circle-xmark</v-icon>{{ $t('csvImport_failed') }}</span>
                    <span class="counter__value">{{ current.failed || 0 }}</span>
                </div>
            </div>

            <div v-if="isActive(current.status)" class="notice notice--info">
                <v-icon size="16" class="notice__icon icon-themed">fa-regular fa-circle-info</v-icon>
                <span>{{ $t('csvImport_runningNote') }}</span>
            </div>

            <div class="filters" role="group" :aria-label="$t('csvImport_status')">
                <button
                    v-for="f in filters"
                    :key="f.value"
                    type="button"
                    class="filter-chip"
                    :class="{ active: filter === f.value }"
                    :aria-pressed="filter === f.value ? 'true' : 'false'"
                    @click="setFilter(f.value)">
                    {{ f.label }} · {{ f.count }}
                </button>
            </div>

            <div class="rows-table">
                <div class="rows-table__head rows-grid">
                    <span>{{ $t('csvImport_line') }}</span>
                    <span>{{ $t('csvImport_account') }}</span>
                    <span>{{ $t('csvImport_status') }}</span>
                    <span>{{ $t('csvImport_reason') }}</span>
                </div>
                <div v-if="!visibleRows.length" class="rows-table__empty">
                    <v-progress-circular v-if="loadingImport" indeterminate size="20" :color="primaryColor" />
                    <span v-else>{{ $t('csvImport_noRows') }}</span>
                </div>
                <div v-for="row in visibleRows" :key="row.line" class="rows-table__row rows-grid">
                    <span>{{ row.line }}</span>
                    <span class="rows-table__account">{{ row.accountLogin }}</span>
                    <span>
                        <span class="pill pill--small" :class="rowPillClass(row.status)">{{ rowStatusLabel(row.status) }}</span>
                    </span>
                    <span class="rows-table__reason">
                        <template v-if="row.status === 'FAILED'">
                            <span>{{ rowReason(row) }}</span>
                            <code v-if="row.errorCode" class="rows-table__code">{{ shortCode(row.errorCode) }}</code>
                        </template>
                        <span v-else class="muted">—</span>
                    </span>
                </div>
            </div>
            <button v-if="filteredRows.length > visibleRows.length" type="button" class="link-btn" @click="visibleCount += ROWS_PAGE">
                {{ $t('csvImport_showMore', { shown: visibleRows.length, total: filteredRows.length }) }}
            </button>

            <div v-if="!isActive(current.status) && current.failed > 0" class="notice notice--info">
                <v-icon size="16" class="notice__icon icon-themed">fa-regular fa-rotate</v-icon>
                <span>{{ $t('csvImport_retryHint') }}</span>
            </div>

            <div class="actions">
                <span v-if="isActive(current.status)" class="muted actions__note">{{ $t('csvImport_waitForImport') }}</span>
                <button
                    v-if="!isActive(current.status) && current.failed > 0"
                    type="button"
                    class="btn btn--secondary"
                    @click="copyFailedAccounts">
                    <v-icon size="14" class="icon-themed">fa-regular fa-copy</v-icon>
                    {{ $t('csvImport_copyFailed') }}
                </button>
                <button
                    type="button"
                    class="btn btn--primary"
                    :disabled="isActive(current.status)"
                    @click="startNewImport">
                    <v-icon size="14" color="#fff">fa-regular fa-arrow-up-from-bracket</v-icon>
                    {{ $t('csvImport_newImport') }}
                </button>
            </div>
        </template>

        <!-- ============ UPLOAD FORM ============ -->
        <template v-else>
            <p class="desc">{{ $t('csvImport_description') }}</p>

            <div class="field">
                <span class="field-label">{{ $t('csvImport_columns') }}</span>
                <div class="columns-grid">
                    <div v-for="col in csvColumns" :key="col.name" class="column-card">
                        <span class="column-card__name">
                            <code>{{ col.name }}</code>
                            <span v-if="col.required" class="required-badge">{{ $t('csvImport_required') }}</span>
                        </span>
                        <span class="column-card__desc">{{ col.description }}</span>
                    </div>
                </div>
            </div>

            <ul class="rules">
                <li>{{ $t('csvImport_ruleHeader') }}</li>
                <li>{{ $t('csvImport_ruleLimit') }}</li>
                <li><strong>{{ $t('csvImport_ruleNoEmail') }}</strong></li>
                <li v-if="serverNames.length > 1">{{ $t('csvImport_ruleServers', { servers: serverNames.join(', ') }) }}</li>
            </ul>

            <button type="button" class="link-btn link-btn--strong" :disabled="downloadingTemplate" @click="downloadTemplate">
                <v-icon size="14" :color="primaryColor">fa-regular fa-download</v-icon>
                {{ $t('csvImport_downloadTemplate') }}
            </button>

            <div class="divider"></div>

            <div v-if="tournamentEnded" class="dropzone dropzone--disabled">
                <v-icon size="30" class="icon-themed">fa-regular fa-calendar-xmark</v-icon>
                <span class="dropzone__title">{{ $t('csvImport_ended') }}</span>
                <span class="dropzone__hint">{{ $t('csvImport_endedDesc') }}</span>
            </div>
            <div v-else-if="file" class="file-row">
                <v-icon size="20" class="icon-themed">fa-regular fa-file-csv</v-icon>
                <span class="file-row__text">
                    <span class="file-row__name">{{ file.name }}</span>
                    <span class="muted">{{ formatSize(file.size) }}</span>
                </span>
                <button type="button" class="icon-btn" :aria-label="$t('csvImport_removeFile')" :disabled="uploading" @click="clearFile">
                    <v-icon size="16" class="icon-themed">fa-regular fa-times</v-icon>
                </button>
            </div>
            <label
                v-else
                class="dropzone"
                :class="{ 'dropzone--over': dragOver }"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="onDrop">
                <span class="dropzone__icon">
                    <v-icon size="20" :color="primaryColor">fa-regular fa-arrow-up-from-bracket</v-icon>
                </span>
                <span class="dropzone__title">
                    {{ $t('csvImport_dropzone') }} <span class="dropzone__browse">{{ $t('csvImport_browse') }}</span>
                </span>
                <span class="dropzone__hint">{{ $t('csvImport_dropzoneHint') }}</span>
                <input ref="fileInput" type="file" accept=".csv,text/csv" class="visually-hidden" @change="onFileInput">
            </label>

            <div v-if="fileError" class="notice notice--error" role="alert">
                <v-icon size="18" class="notice__icon">fa-regular fa-circle-exclamation</v-icon>
                <span>{{ fileError }}</span>
            </div>

            <!-- 400: the whole file was rejected and nobody was registered -->
            <template v-if="uploadErrors.length">
                <div class="notice notice--error" role="alert">
                    <v-icon size="18" class="notice__icon">fa-regular fa-circle-exclamation</v-icon>
                    <span class="notice__body">
                        <strong>{{ $t('csvImport_rejectedTitle') }}</strong>
                        <span>{{ $t('csvImport_rejectedDesc') }}</span>
                    </span>
                </div>
                <div class="field">
                    <span class="field-label">{{ $t('csvImport_problemsFound') }} · {{ uploadErrors.length }}</span>
                    <div class="rows-table">
                        <div class="rows-table__head errors-grid">
                            <span>{{ $t('csvImport_line') }}</span>
                            <span>{{ $t('csvImport_column') }}</span>
                            <span>{{ $t('csvImport_problem') }}</span>
                        </div>
                        <div v-for="(err, i) in uploadErrors" :key="i" class="rows-table__row errors-grid">
                            <span class="strong">{{ errorLineLabel(err.line) }}</span>
                            <code class="rows-table__column">{{ csvColumnName(err.field) }}</code>
                            <span class="rows-table__reason">{{ err.message }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <div class="actions">
                <button
                    v-if="uploadErrors.length && !tournamentEnded"
                    type="button"
                    class="btn btn--secondary"
                    :disabled="uploading"
                    @click="chooseFile">
                    {{ $t('csvImport_chooseCorrected') }}
                </button>
                <button
                    type="button"
                    class="btn btn--primary"
                    :disabled="!file || uploading || tournamentEnded"
                    @click="startImport">
                    <v-progress-circular v-if="uploading" indeterminate size="14" width="2" color="#fff" />
                    <v-icon v-else size="14" color="#fff">fa-regular fa-arrow-up-from-bracket</v-icon>
                    {{ $t('csvImport_start') }}
                </button>
            </div>
        </template>

        <!-- ============ PREVIOUS IMPORTS ============ -->
        <template v-if="history.length">
            <div class="divider"></div>
            <button type="button" class="history-toggle" :aria-expanded="historyOpen ? 'true' : 'false'" @click="historyOpen = !historyOpen">
                <span class="history-toggle__label">
                    {{ $t('csvImport_previous') }}
                    <span class="muted">{{ historyTotal }}</span>
                </span>
                <v-icon size="16" class="icon-themed" :class="{ 'rotate-180': historyOpen }">fa-regular fa-chevron-down</v-icon>
            </button>
            <div v-if="historyOpen" class="history-list">
                <button
                    v-for="item in history"
                    :key="item.importId"
                    type="button"
                    class="history-item"
                    :class="{ active: current && current.importId === item.importId }"
                    @click="openImport(item.importId)">
                    <v-icon size="18" class="icon-themed">fa-regular fa-file-csv</v-icon>
                    <span class="history-item__text">
                        <span class="history-item__name">{{ item.fileName || $t('csvImport_importNumber', { id: item.importId }) }}</span>
                        <span class="muted">#{{ item.importId }} · {{ formatDate(item.createdDate) }}<template v-if="item.createdBy"> · {{ item.createdBy }}</template></span>
                    </span>
                    <span class="history-item__counts">{{ $t('csvImport_historyCounts', { enrolled: item.enrolled || 0, failed: item.failed || 0 }) }}</span>
                    <span class="pill pill--small" :class="importPillClass(item.status)">{{ $t('csvImport_status_' + item.status) }}</span>
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import TournamentRepository from '../../../repositories/tournament';
import { downloadCsv } from '@/utils/csv';

const POLL_MS = 4000;
const MAX_FILE_BYTES = 1024 * 1024;
const ACTIVE_STATUSES = ['QUEUED', 'RUNNING'];

// errorCode / backend message -> friendly i18n key (FRONTEND_HANDOFF §2.5). Unknown ones show the raw message.
const ERROR_CODE_KEYS = {
    'TOURNAMENT_REGISTRATION.ACCOUNT_LOGIN_DUPLICATE_IN_TOURNAMENT': 'csvImport_reason_duplicate',
    'TOURNAMENT_REGISTRATION.MINIMUM_BALANCE_NOT_REACHED': 'csvImport_reason_minBalance',
    'TOURNAMENT_REGISTRATION.REGISTRATION_NOT_OPEN': 'csvImport_reason_notOpen',
    'TOURNAMENT_REGISTRATION.ACCOUNT_TYPE_NOT_PERMITTED': 'csvImport_reason_accountType',
};
const ACCOUNT_NOT_FOUND_MESSAGE = 'The account was not found in the platform';

// Field names of the 400 errors -> the CSV column the user sees in their file.
const FIELD_COLUMNS = {
    accountLogin: 'account_login',
    firstName: 'first_name',
    lastName: 'last_name',
    nickname: 'nickname',
    country: 'country',
    server: 'server',
    email: 'email',
};

export default {
    name: 'ParticipantsCsvImport',
    props: {
        tournamentId: { type: Number, required: true },
        externalId: { type: String, default: '' },
        tournamentEnded: { type: Boolean, default: false },
        primaryColor: { type: String, default: '#8B5CF6' }
    },
    data() {
        return {
            ROWS_PAGE: 100,
            file: null,
            fileError: '',
            dragOver: false,
            uploading: false,
            uploadErrors: [],
            downloadingTemplate: false,
            current: null,
            loadingImport: false,
            filter: 'ALL',
            visibleCount: 100,
            history: [],
            historyTotal: 0,
            historyOpen: false,
            serverNames: [],
            pollTimer: null,
            destroyed: false
        };
    },
    computed: {
        csvColumns() {
            return [
                { name: 'account_login', required: true, description: this.$t('csvImport_colAccountLogin') },
                { name: 'nickname', description: this.$t('csvImport_colNickname') },
                { name: 'first_name', description: this.$t('csvImport_colFirstName') },
                { name: 'last_name', description: this.$t('csvImport_colLastName') },
                { name: 'country', description: this.$t('csvImport_colCountry') },
                { name: 'server', description: this.$t('csvImport_colServer') }
            ];
        },
        rows() {
            return (this.current && Array.isArray(this.current.rows))
                ? [...this.current.rows].sort((a, b) => a.line - b.line)
                : [];
        },
        filteredRows() {
            return this.filter === 'ALL' ? this.rows : this.rows.filter(r => r.status === this.filter);
        },
        visibleRows() {
            return this.filteredRows.slice(0, this.visibleCount);
        },
        filters() {
            const c = this.current || {};
            return [
                { value: 'ALL', label: this.$t('csvImport_all'), count: c.totalRows || 0 },
                { value: 'ENROLLED', label: this.$t('csvImport_registered'), count: c.enrolled || 0 },
                { value: 'PENDING', label: this.$t('csvImport_inProgress'), count: c.pending || 0 },
                { value: 'FAILED', label: this.$t('csvImport_failed'), count: c.failed || 0 }
            ];
        },
        processedCount() {
            if (!this.current) return 0;
            return (this.current.enrolled || 0) + (this.current.failed || 0);
        },
        progressPercent() {
            const total = this.current?.totalRows || 0;
            return total ? Math.round((this.processedCount / total) * 100) : 0;
        }
    },
    mounted() {
        this.loadHistory(true);
        this.loadServers();
    },
    beforeDestroy() {
        this.destroyed = true;
        clearTimeout(this.pollTimer);
    },
    methods: {
        isActive(status) {
            return ACTIVE_STATUSES.includes(status);
        },

        // ---------- File selection ----------
        chooseFile() {
            // The dropzone (and its input) only renders without a file: clear first, then open the picker.
            this.file = null;
            this.$nextTick(() => {
                if (this.$refs.fileInput) this.$refs.fileInput.click();
            });
        },
        onFileInput(event) {
            const picked = event.target.files && event.target.files[0];
            event.target.value = '';
            if (picked) this.setFile(picked);
        },
        onDrop(event) {
            this.dragOver = false;
            const dropped = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (dropped) this.setFile(dropped);
        },
        setFile(file) {
            this.fileError = '';
            if (!/\.csv$/i.test(file.name)) {
                this.fileError = this.$t('csvImport_invalidType');
                return;
            }
            if (file.size > MAX_FILE_BYTES) {
                this.fileError = this.$t('csvImport_tooLarge');
                return;
            }
            this.file = file;
            this.uploadErrors = [];
        },
        clearFile() {
            this.file = null;
            this.fileError = '';
        },

        // ---------- Upload ----------
        async startImport() {
            if (!this.file || this.uploading || this.tournamentEnded) return;
            const file = this.file;
            this.uploading = true;
            this.fileError = '';
            this.uploadErrors = [];
            try {
                const response = await TournamentRepository.importParticipantsCsv(this.tournamentId, file);
                const data = response.data || {};
                this.file = null;
                this.showImport({
                    ...data,
                    fileName: file.name,
                    enrolled: 0,
                    failed: 0,
                    pending: data.totalRows || 0,
                    createdDate: new Date().toISOString(),
                    rows: []
                });
                this.refreshCurrent();
            } catch (error) {
                this.handleUploadError(error);
            } finally {
                this.uploading = false;
            }
        },
        handleUploadError(error) {
            const status = error.response?.status;
            const data = error.response?.data || {};
            if (status === 400 && Array.isArray(data.errors) && data.errors.length) {
                this.uploadErrors = [...data.errors].sort((a, b) => (a.line || 0) - (b.line || 0));
                return;
            }
            if (status === 409) {
                this.fileError = this.$t('csvImport_errorEnded');
            } else if (status === 403) {
                this.fileError = data.detail || this.$t('csvImport_errorForbidden');
            } else if (status === 413) {
                this.fileError = this.$t('csvImport_tooLarge');
            } else {
                this.fileError = data.detail || data.message || this.$t('csvImport_errorGeneric');
            }
        },

        // ---------- Import detail + polling ----------
        showImport(importData) {
            clearTimeout(this.pollTimer);
            this.current = importData;
            this.visibleCount = this.ROWS_PAGE;
            this.filter = (!this.isActive(importData.status) && importData.failed > 0) ? 'FAILED' : 'ALL';
        },
        async openImport(importId) {
            if (this.current && this.current.importId === importId) return;
            const summary = this.history.find(h => h.importId === importId);
            this.showImport({ ...(summary || { importId }), rows: [] });
            this.loadingImport = true;
            await this.refreshCurrent();
            this.loadingImport = false;
        },
        async refreshCurrent() {
            clearTimeout(this.pollTimer);
            if (!this.current || this.destroyed) return;
            const importId = this.current.importId;
            const wasActive = this.isActive(this.current.status);
            try {
                const response = await TournamentRepository.getParticipantImport(this.tournamentId, importId);
                // The user may have opened another import while this request was in flight.
                if (this.destroyed || !this.current || this.current.importId !== importId) return;
                const fileName = response.data.fileName || this.current.fileName;
                this.current = { ...response.data, fileName };
                if (this.isActive(this.current.status)) {
                    this.schedulePoll();
                } else if (wasActive) {
                    this.onImportFinished();
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error fetching participant import:', error);
                if (this.current && this.current.importId === importId && wasActive) {
                    this.schedulePoll();
                }
            }
        },
        schedulePoll() {
            clearTimeout(this.pollTimer);
            if (this.destroyed) return;
            this.pollTimer = setTimeout(this.refreshCurrent, POLL_MS);
        },
        onImportFinished() {
            if (this.current.failed > 0) this.filter = 'FAILED';
            if (this.current.enrolled > 0) this.$emit('participantsChanged');
            this.loadHistory(false);
        },
        startNewImport() {
            clearTimeout(this.pollTimer);
            this.current = null;
            this.file = null;
            this.fileError = '';
            this.uploadErrors = [];
        },

        // ---------- History ----------
        // `resume`: when the panel opens, pick up an import that is still queued/running.
        async loadHistory(resume) {
            try {
                const response = await TournamentRepository.getParticipantImports(this.tournamentId, 0, 10);
                const page = response.data || {};
                this.history = Array.isArray(page.content) ? page.content : [];
                this.historyTotal = page.totalElements || this.history.length;
                if (resume && !this.current) {
                    const running = this.history.find(h => this.isActive(h.status));
                    if (running) this.openImport(running.importId);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error fetching participant imports:', error);
            }
        },
        async loadServers() {
            if (!this.externalId) return;
            try {
                const response = await TournamentRepository.getTournamentScopes(this.externalId);
                if (response.status < 400 && Array.isArray(response.data)) {
                    this.serverNames = response.data.map(s => s.serverName).filter(Boolean);
                }
            } catch (_) {
                this.serverNames = [];
            }
        },

        // ---------- Actions ----------
        async downloadTemplate() {
            this.downloadingTemplate = true;
            try {
                const response = await TournamentRepository.getParticipantImportTemplate();
                // downloadCsv adds its own BOM.
                const text = String(response.data || '').replace(/^\uFEFF/, '');
                downloadCsv('participants-template.csv', text);
            } catch (error) {
                this.fileError = this.$t('csvImport_errorGeneric');
            } finally {
                this.downloadingTemplate = false;
            }
        },
        copyFailedAccounts() {
            const accounts = this.rows.filter(r => r.status === 'FAILED').map(r => r.accountLogin);
            navigator.clipboard.writeText(accounts.join('\n')).then(() => {
                this.$emit('copied');
            }).catch(err => {
                // eslint-disable-next-line no-console
                console.error('Could not copy text: ', err);
            });
        },
        setFilter(value) {
            this.filter = value;
            this.visibleCount = this.ROWS_PAGE;
        },

        // ---------- Formatting ----------
        rowReason(row) {
            const key = ERROR_CODE_KEYS[row.errorCode];
            if (key) return this.$t(key);
            if (row.message && row.message.includes(ACCOUNT_NOT_FOUND_MESSAGE)) return this.$t('csvImport_reason_notFound');
            return row.message || '';
        },
        shortCode(code) {
            return String(code).replace(/^TOURNAMENT_REGISTRATION\./, '');
        },
        rowStatusLabel(status) {
            if (status === 'ENROLLED') return this.$t('csvImport_registered');
            if (status === 'FAILED') return this.$t('csvImport_failed');
            return this.$t('csvImport_inProgress');
        },
        rowPillClass(status) {
            if (status === 'ENROLLED') return 'pill--success';
            if (status === 'FAILED') return 'pill--danger';
            return 'pill--warning';
        },
        importPillClass(status) {
            if (status === 'COMPLETED') return 'pill--success';
            if (status === 'FAILED') return 'pill--danger';
            if (status === 'RUNNING') return 'pill--warning';
            return 'pill--neutral';
        },
        importMeta(item) {
            const parts = [this.$t('csvImport_importNumber', { id: item.importId })];
            if (item.finishedDate) {
                parts.push(this.$t('csvImport_finishedAt', { date: this.formatDate(item.finishedDate) }));
            } else if (item.createdDate) {
                parts.push(this.$t('csvImport_startedAt', { date: this.formatDate(item.createdDate) }));
            }
            return parts.join(' · ');
        },
        segmentWidth(count) {
            const total = this.current?.totalRows || 0;
            return total ? `${Math.min(100, ((count || 0) / total) * 100)}%` : '0%';
        },
        errorLineLabel(line) {
            if (!line) return this.$t('csvImport_file');
            return line === 1 ? `1 (${this.$t('csvImport_header')})` : String(line);
        },
        csvColumnName(field) {
            if (!field) return '—';
            return FIELD_COLUMNS[field] || field;
        },
        formatDate(value) {
            if (!value) return '';
            const date = new Date(value);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleString(this.$i18n.locale || undefined, {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        },
        formatSize(bytes) {
            if (bytes < 1024) return `${bytes} B`;
            return `${Math.ceil(bytes / 1024)} KB`;
        }
    }
}
</script>

<style lang="scss" scoped>
// Colors come from TournamentShare's --share-* variables (light/dark), with fallbacks.
.csv-import {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: 'Avenir', sans-serif;
    color: var(--share-text, #3b3b3b);
}

.desc {
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
}

.muted {
    color: var(--share-muted, #666);
}

.strong {
    font-weight: 700;
}

.divider {
    height: 1px;
    width: 100%;
    background-color: var(--share-border, #ababab);
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-weight: 800;
    font-size: 13px;
    line-height: 1.5;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

// CSV columns
.columns-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.column-card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background-color: var(--share-chip-bg, #ebebeb);
    border-radius: 6px;
    padding: 10px 12px;

    &__name code {
        font-family: 'Courier New', monospace;
        font-weight: 700;
        font-size: 13px;
        color: var(--share-title-color, #14161b);
        background: none;
        padding: 0;
    }

    &__desc {
        font-size: 12px;
        line-height: 1.4;
        color: var(--share-muted, #666);
    }
}

.required-badge {
    display: inline-block;
    margin-left: 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: #fff;
    background-color: #3b3b3b;
    border-radius: 3px;
    padding: 1px 5px;
    vertical-align: middle;
}

.rules {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.6;
    color: var(--share-muted, #666);

    strong {
        color: var(--share-text, #3b3b3b);
    }
}

// Buttons
.link-btn {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 4px 0;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: var(--share-text, #3b3b3b);
    text-decoration: underline;

    &--strong {
        font-size: 14px;
        font-weight: 700;
        text-decoration: none;
        color: var(--import-accent);
    }

    &:disabled {
        opacity: 0.5;
        cursor: wait;
    }
}

.actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    &__note {
        flex: 1;
        font-size: 13px;
    }
}

.btn {
    height: 40px;
    padding: 0 16px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    &--primary {
        border: none;
        background-color: var(--import-accent);
        color: #fff;
    }

    &--secondary {
        border: 1px solid var(--share-border, #ababab);
        background: transparent;
        color: var(--share-text, #3b3b3b);
    }

    &:hover:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.icon-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;

    &:hover:not(:disabled) {
        opacity: 0.7;
    }
}

// Dropzone / selected file
.dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1.5px dashed var(--share-border, #ababab);
    border-radius: 8px;
    padding: 24px 16px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;

    &:hover,
    &--over,
    &:focus-within {
        border-color: var(--import-accent);
        background-color: var(--share-hover, rgba(0, 0, 0, 0.02));
    }

    &--disabled {
        cursor: default;
        background-color: var(--share-chip-bg, #ebebeb);

        &:hover {
            border-color: var(--share-border, #ababab);
            background-color: var(--share-chip-bg, #ebebeb);
        }
    }

    &__icon {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--share-chip-bg, #ebebeb);
    }

    &__title {
        font-size: 14px;
        font-weight: 700;
        color: var(--share-title-color, #14161b);
    }

    &__browse {
        color: var(--import-accent);
        text-decoration: underline;
    }

    &__hint {
        font-size: 12px;
        color: var(--share-muted, #666);
        max-width: 340px;
        line-height: 1.4;
    }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

.file-row {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid var(--share-input-border, #3b3b3b);
    border-radius: 6px;
    padding: 8px 8px 8px 12px;

    &__text {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        font-size: 12px;
    }

    &__name {
        font-size: 14px;
        font-weight: 700;
        color: var(--share-title-color, #14161b);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// Notices
.notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.5;

    &__icon {
        flex-shrink: 0;
        margin-top: 1px;
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 2px;

        strong {
            font-size: 14px;
        }
    }

    &--info {
        background-color: var(--share-chip-bg, #ebebeb);
    }

    &--success {
        background-color: #e2f5e5;
        color: #174f24;

        .notice__icon {
            color: #1e6b2f !important;
        }
    }

    &--error {
        background-color: #fde4e2;
        color: #5e1510;

        strong {
            color: #8c1d16;
        }

        .notice__icon {
            color: #a8261c !important;
        }
    }
}

// Status pills
.pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 12px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    &--small {
        padding: 2px 8px;
        font-size: 11px;
    }

    &--success {
        color: #1e6b2f;
        background-color: #e2f5e5;
    }

    &--warning {
        color: #7a4f00;
        background-color: #fff1d0;
    }

    &--danger {
        color: #8c1d16;
        background-color: #fde4e2;
    }

    &--neutral {
        color: #3b3b3b;
        background-color: #ebebeb;
    }

    &__spin {
        color: inherit !important;
        animation: csv-import-spin 1s linear infinite;
    }
}

@keyframes csv-import-spin {
    to {
        transform: rotate(360deg);
    }
}

// Import header, progress, counters
.import-head {
    display: flex;
    align-items: center;
    gap: 10px;

    &__text {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
    }

    &__name {
        font-size: 15px;
        font-weight: 700;
        color: var(--share-title-color, #14161b);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__meta {
        font-size: 12px;
        color: var(--share-muted, #666);
    }
}

.progress {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &__labels {
        display: flex;
        justify-content: space-between;
        font-size: 13px;

        strong {
            color: var(--share-title-color, #14161b);
        }
    }

    &__track {
        display: flex;
        height: 10px;
        border-radius: 5px;
        overflow: hidden;
        background-color: var(--share-chip-bg, #e4e4e4);
    }

    &__seg {
        height: 100%;
        transition: width 0.4s ease;

        &--ok {
            background-color: #2e8b47;
        }

        &--failed {
            background-color: #a8261c;
        }
    }
}

.counters {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.counter {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 1px solid var(--share-border, #d6d6d6);
    border-radius: 8px;
    padding: 10px 12px;

    &__label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 700;
    }

    &__icon {
        &--ok { color: #2e8b47 !important; }
        &--pending { color: #b07400 !important; }
        &--failed { color: #a8261c !important; }
    }

    &__value {
        font-size: 24px;
        font-weight: 800;
        line-height: 1.15;
        color: var(--share-title-color, #14161b);
    }

    &--muted {
        opacity: 0.6;
    }

    &--alert {
        border-color: #a8261c;
    }
}

// Filters
.filters {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.filter-chip {
    height: 32px;
    padding: 0 12px;
    border-radius: 16px;
    border: 1px solid var(--share-border, #ababab);
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--share-text, #3b3b3b);
    cursor: pointer;

    &.active {
        border-color: var(--import-accent);
        background-color: var(--import-accent);
        color: #fff;
        font-weight: 700;
    }
}

// Tables (rows of an import and 400 errors)
.rows-table {
    border: 1px solid var(--share-input-border, #3b3b3b);
    border-radius: 4px;
    overflow: hidden;
    font-size: 13px;
    max-height: 360px;
    overflow-y: auto;

    &__head {
        position: sticky;
        top: 0;
        background-color: var(--share-tab-bg, #ebebeb);
        font-weight: 800;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
    }

    &__row {
        border-top: 1px solid var(--share-border, #ababab);
    }

    &__empty {
        display: flex;
        justify-content: center;
        padding: 16px;
        color: var(--share-muted, #666);
    }

    &__account {
        font-weight: 600;
        color: var(--share-title-color, #14161b);
        word-break: break-all;
    }

    &__reason {
        display: flex;
        flex-direction: column;
        gap: 2px;
        line-height: 1.4;
        min-width: 0;
    }

    &__code,
    &__column {
        font-family: 'Courier New', monospace;
        font-size: 11px;
        background: none;
        padding: 0;
        word-break: break-all;
    }

    &__code {
        color: var(--share-muted, #666);
    }

    &__column {
        font-size: 12px;
        font-weight: 700;
        color: var(--share-title-color, #14161b);
    }
}

.rows-grid,
.errors-grid {
    display: grid;
    gap: 10px;
    padding: 8px 10px;
    align-items: start;
}

.rows-grid {
    grid-template-columns: 36px 92px 88px minmax(0, 1fr);
}

.errors-grid {
    grid-template-columns: 72px 100px minmax(0, 1fr);
}

// Previous imports
.history-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: 1px solid var(--share-border, #ababab);
    border-radius: 4px;
    padding: 12px;
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    color: inherit;

    &:hover {
        background-color: var(--share-hover, rgba(0, 0, 0, 0.02));
    }

    &__label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 800;

        .muted {
            font-size: 12px;
            font-weight: 500;
        }
    }
}

.rotate-180 {
    transform: rotate(180deg);
    transition: transform 0.2s ease;
}

.history-list {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--share-border, #d6d6d6);
    border-radius: 6px;
    overflow: hidden;
}

.history-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: transparent;
    border: none;
    text-align: left;
    font-family: inherit;
    color: inherit;
    cursor: pointer;

    & + & {
        border-top: 1px solid var(--share-border, #e4e4e4);
    }

    &:hover,
    &.active {
        background-color: var(--share-hover, rgba(0, 0, 0, 0.03));
    }

    &.active {
        box-shadow: inset 3px 0 0 var(--import-accent);
    }

    &__text {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        font-size: 12px;
    }

    &__name {
        font-size: 14px;
        font-weight: 700;
        color: var(--share-title-color, #14161b);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__counts {
        font-size: 12px;
        white-space: nowrap;
    }
}

::v-deep .icon-themed {
    color: var(--share-icon-color, #333) !important;
}

@media (max-width: 600px) {
    .columns-grid {
        grid-template-columns: 1fr;
    }

    .rows-grid {
        grid-template-columns: 32px 1fr 84px;
    }

    .rows-grid > :nth-child(4) {
        grid-column: 2 / 4;
    }

    .history-item__counts {
        display: none;
    }
}
</style>
